// src/ApiPanel/utils/apiPanelFetch.js
// Fetch wrapper scoped to the API Panel. Same header profile as the main
// app's utils/apiFetch.js — deliberately.
//
// ─────────────────────────────────────────────────────────────────────────
// WHY THIS FILE NO LONGER SENDS X-Client-ID / X-Platform
// ─────────────────────────────────────────────────────────────────────────
// It used to, and that caused a CORS failure that looked like a server
// problem but was not:
//
//   Access to fetch at 'https://pro.mokshiq.in/...' has been blocked by CORS
//   policy: Request header field x-client-id is not allowed by
//   Access-Control-Allow-Headers in preflight response.
//
// settings.py sets CORS_ALLOW_ALL_ORIGINS = True but never sets
// CORS_ALLOW_HEADERS, so django-cors-headers uses its default allow-list:
//
//   accept, accept-encoding, authorization, content-type, dnt, origin,
//   user-agent, x-csrftoken, x-requested-with
//
// X-Client-ID and X-Platform are not on it. Both are "non-simple" headers, so
// the browser sends a preflight OPTIONS first; the server answers without
// them in Access-Control-Allow-Headers; the browser refuses to send the real
// request. The response never reaches this code, which is why it surfaces as
// a network/CORS error rather than a 4xx you could read.
//
// Neither header was necessary:
//
//   X-Client-ID  account/utils/jwt.get_client_id() reads the Origin header
//                FIRST and only falls back to X-Client-ID. Browsers set
//                Origin automatically on every cross-origin request, so for
//                this panel the header was pure duplication — and the JWT
//                binds to whatever get_client_id() returned at login, which
//                is the Origin either way.
//
//   X-Platform   account/login_utils.detect_platform() returns 'web' when the
//                header is absent, which is exactly what a browser session
//                wants. Sending it changed nothing.
//
// So: Authorization + Content-Type only, same as the main app's wrapper, and
// the preflight problem disappears with no backend change.
//
// If you ever DO need to send X-Client-ID — the panel served from the same
// origin as the API, where the browser omits Origin, or a non-browser client
// — set REACT_APP_API_PANEL_CLIENT_ID and add this to settings.py, or the
// same CORS error comes straight back:
//
//   from corsheaders.defaults import default_headers
//   CORS_ALLOW_HEADERS = list(default_headers) + ["x-client-id"]
//
// ─────────────────────────────────────────────────────────────────────────
import {
  API_PANEL_BASE_URL,
  API_PANEL_CLIENT_ID,
  API_PANEL_LOGIN_PATH,
  getApiPanelToken,
  clearApiPanelToken,
} from "../constants/apiPanelAuth";

/** Build a query string, dropping empty values so filters stay clean. */
export function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, value);
  });
  const str = search.toString();
  return str ? `?${str}` : "";
}

function toFormBody(payload) {
  const body = new URLSearchParams();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    body.append(key, value);
  });
  return body.toString();
}

/**
 * @param {string} path      endpoint path, or a full URL
 * @param {object} options   standard fetch options, plus:
 *   - form: object   send as application/x-www-form-urlencoded (request.POST)
 *   - json: object   send as application/json (DRF request.data)
 *   - raw: true      return the Response untouched (for file downloads)
 */
export async function apiPanelFetch(path, options = {}) {
  const { form, json, raw, headers: extraHeaders, ...rest } = options;
  const token = getApiPanelToken();
  const url = path.startsWith("http") ? path : `${API_PANEL_BASE_URL}${path}`;

  // Only headers on the server's CORS allow-list. Adding anything else here
  // means adding it to CORS_ALLOW_HEADERS too — see the note at the top.
  const headers = { Accept: "application/json", ...(extraHeaders || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  // Opt-in only, and off unless you have set the env var AND the matching
  // CORS_ALLOW_HEADERS entry.
  if (API_PANEL_CLIENT_ID) headers["X-Client-ID"] = API_PANEL_CLIENT_ID;

  let body = rest.body;
  if (form) {
    body = toFormBody(form);
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  } else if (json) {
    body = JSON.stringify(json);
    headers["Content-Type"] = "application/json";
  } else if (body instanceof FormData) {
    // Let the browser set the multipart boundary — setting it by hand breaks
    // the upload with a parse error that is genuinely hard to diagnose.
    delete headers["Content-Type"];
  }

  let res;
  try {
    res = await fetch(url, { ...rest, body, headers });
  } catch (networkError) {
    // fetch() rejects with an opaque TypeError for a blocked CORS preflight,
    // a DNS failure and an offline browser alike — the browser deliberately
    // withholds the detail from JS. Say so rather than showing "Failed to
    // fetch", which sends people hunting in the wrong place.
    throw new Error(
      `Could not reach the API at ${API_PANEL_BASE_URL}. Check the console's ` +
      `Network tab: a blocked CORS preflight, an unreachable host and no ` +
      `internet all look identical from here.`
    );
  }

  if (res.status === 401) {
    clearApiPanelToken();
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith(API_PANEL_LOGIN_PATH)
    ) {
      window.location.assign(API_PANEL_LOGIN_PATH);
    }
    throw new Error("Your session has expired. Please sign in again.");
  }

  if (raw) {
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return res;
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* empty or non-JSON body */
  }

  if (!res.ok) {
    throw new Error(
      (data && (data.message || data.error)) || `Request failed (${res.status})`
    );
  }

  // The Mokshiq API signals business-rule refusals with a 200 and a flag in
  // the body, so an HTTP-only check would treat a refusal as success and
  // render an empty table instead of the reason. Two different conventions
  // are in use and both have to be handled:
  //
  //   most views          {"status": false,   "message": "..."}
  //   ticket_manager_action {"status": "error", "message": "..."}
  //
  // `"error" === false` is false, so checking only the boolean lets every
  // ticket failure through silently.
  if (data && (data.status === false || data.status === "error")) {
    throw new Error(data.message || data.data || "Request failed");
  }

  return data;
}

/**
 * Download an export to the user's machine.
 *
 * Goes through fetch rather than a plain <a href> because the endpoint needs
 * the Authorization header — a link element cannot send one, and putting the
 * token in the query string would leak it into server logs, browser history
 * and any Referer header.
 */
export async function apiPanelDownload(path, filename) {
  const res = await apiPanelFetch(path, { raw: true });
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename || "export.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick; revoking synchronously can cancel the download
  // in some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
